import React, { useState, useEffect, useRef, useCallback } from "react";
import Modal from "react-modal";
import Camera, { FACING_MODES } from "react-html5-camera-photo";
import "react-html5-camera-photo/build/css/index.css";
import { i18n } from "../../translate/i18n";

Modal.setAppElement("#root");

const TAB_PHOTO = "photo";
const TAB_VIDEO = "video";

const customStyles = {
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1300,
  },
  content: {
    width: "90%",
    maxWidth: "780px",
    maxHeight: "90vh",
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    borderRadius: "8px",
    boxShadow: "0 0 10px rgba(0, 0, 0, 0.3)",
    border: "none",
    padding: "12px",
    background: "#1a1a1a",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    overflow: "hidden",
  },
};

function pickVideoMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  for (const t of candidates) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return "";
}

const tabBtn = (active) => ({
  flex: 1,
  padding: "10px 12px",
  margin: "0 4px",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: 600,
  background: active ? "#e91e63" : "#333",
  color: "#fff",
});

const ModalCamera = ({ isOpen, onRequestClose, onCapture, onError }) => {
  const [tab, setTab] = useState(TAB_PHOTO);
  const [photoKey, setPhotoKey] = useState(0);
  const [capturedImage, setCapturedImage] = useState(null);
  const videoPreviewRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  const onCaptureRef = useRef(onCapture);
  const onRequestCloseRef = useRef(onRequestClose);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onCaptureRef.current = onCapture;
    onRequestCloseRef.current = onRequestClose;
    onErrorRef.current = onError;
  }, [onCapture, onRequestClose, onError]);

  useEffect(() => {
    if (isOpen && tab === TAB_PHOTO) {
      setPhotoKey((k) => k + 1);
    }
  }, [isOpen, tab]);

  useEffect(() => {
    if (!isOpen) {
      setTab(TAB_PHOTO);
      setCapturedImage(null);
      setRecording(false);
      setVideoReady(false);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = null;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!capturedImage) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(capturedImage);
        const blob = await res.blob();
        if (!cancelled && blob.size > 0) {
          onCaptureRef.current(blob);
        }
      } catch (e) {
        if (!cancelled && typeof onErrorRef.current === "function") {
          onErrorRef.current(e);
        }
      } finally {
        if (!cancelled) {
          setCapturedImage(null);
          onRequestCloseRef.current();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [capturedImage]);

  useEffect(() => {
    if (!isOpen || tab !== TAB_VIDEO) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: true,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        setVideoReady(true);
        const el = videoPreviewRef.current;
        if (el) {
          el.srcObject = stream;
        }
      } catch (e) {
        if (typeof onErrorRef.current === "function") {
          onErrorRef.current(e);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = null;
      }
      setVideoReady(false);
      setRecording(false);
      mediaRecorderRef.current = null;
    };
  }, [isOpen, tab]);

  const handleTakePhoto = (dataUri) => {
    setCapturedImage(dataUri);
  };

  const handleCameraLibError = useCallback((err) => {
    if (typeof onErrorRef.current === "function") {
      onErrorRef.current(err);
    }
  }, []);

  const startVideoRecord = () => {
    if (!streamRef.current || recording) return;
    if (typeof MediaRecorder === "undefined") {
      if (typeof onErrorRef.current === "function") {
        onErrorRef.current(new Error("MediaRecorder não suportado neste navegador."));
      }
      return;
    }
    const mime = pickVideoMimeType();
    let mr;
    try {
      mr = mime
        ? new MediaRecorder(streamRef.current, { mimeType: mime })
        : new MediaRecorder(streamRef.current);
    } catch {
      try {
        mr = new MediaRecorder(streamRef.current);
      } catch (e) {
        if (typeof onErrorRef.current === "function") onErrorRef.current(e);
        return;
      }
    }
    mediaRecorderRef.current = mr;
    const chunks = [];
    mr.ondataavailable = (ev) => {
      if (ev.data && ev.data.size) chunks.push(ev.data);
    };
    mr.onstop = () => {
      setRecording(false);
      const outType = mr.mimeType || "video/webm";
      const blob = new Blob(chunks, { type: outType });
      if (blob.size > 0) {
        onCaptureRef.current(blob);
        onRequestCloseRef.current();
      }
    };
    try {
      mr.start(250);
      setRecording(true);
    } catch (e) {
      if (typeof onErrorRef.current === "function") onErrorRef.current(e);
    }
  };

  const stopVideoRecord = () => {
    if (mediaRecorderRef.current && recording) {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        setRecording(false);
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} style={customStyles}>
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 12,
          justifyContent: "center",
        }}
      >
        <button
          type="button"
          style={tabBtn(tab === TAB_PHOTO)}
          onClick={() => setTab(TAB_PHOTO)}
        >
          {i18n.t("messageInput.camera.tabPhoto")}
        </button>
        <button
          type="button"
          style={tabBtn(tab === TAB_VIDEO)}
          onClick={() => setTab(TAB_VIDEO)}
        >
          {i18n.t("messageInput.camera.tabVideo")}
        </button>
      </div>

      {tab === TAB_PHOTO && (
        <div
          style={{
            flex: 1,
            minHeight: 320,
            position: "relative",
            background: "#000",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <Camera
            key={photoKey}
            onTakePhoto={handleTakePhoto}
            onCameraError={handleCameraLibError}
            idealFacingMode={FACING_MODES.USER}
            isImageMirror={false}
            isDisplayStartCameraError
          />
        </div>
      )}

      {tab === TAB_VIDEO && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            color: "#fff",
          }}
        >
          <video
            ref={videoPreviewRef}
            autoPlay
            muted
            playsInline
            style={{
              width: "100%",
              maxHeight: "50vh",
              borderRadius: 8,
              background: "#000",
            }}
          />
          {!videoReady && (
            <span style={{ fontSize: 14 }}>
              {i18n.t("messageInput.camera.preparing")}
            </span>
          )}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
            {!recording ? (
              <button
                type="button"
                disabled={!videoReady}
                style={{
                  ...tabBtn(true),
                  opacity: videoReady ? 1 : 0.5,
                  cursor: videoReady ? "pointer" : "not-allowed",
                }}
                onClick={startVideoRecord}
              >
                {i18n.t("messageInput.camera.startRecord")}
              </button>
            ) : (
              <button
                type="button"
                style={tabBtn(true)}
                onClick={stopVideoRecord}
              >
                {i18n.t("messageInput.camera.stopSend")}
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ModalCamera;
